// @ts-nocheck
import React from 'react';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import styles from '@/components/atoms/LineChart/lineChart.module.css';

export interface DataPoint {
  x: number | string;
  y: number;
}

export interface LineChartSeries {
  id: string;
  data: DataPoint[];
  color?: string;
  label?: string;
}

export interface LineChartProps {
  /**
   * Array of series data to be displayed in the chart
   */
  series: LineChartSeries[];
  /**
   * Width of the chart in pixels
   */
  width?: number;
  /**
   * Height of the chart in pixels
   */
  height?: number;
  /**
   * Title of the chart
   */
  title?: string;
  /**
   * X-axis label
   */
  xLabel?: string;
  /**
   * Y-axis label
   */
  yLabel?: string;
  /**
   * Custom className for additional styling
   */
  className?: string;
  /**
   * Show/hide grid lines
   */
  showVerticalGrid?: boolean;
  showHorizontalGrid?: boolean /**
   * Show/hide tooltips
   */;
  showTooltip?: boolean;
  /**
   * Show/hide legend
   */
  showLegend?: boolean;
  /**
   * Custom margin for the chart
   */
  margin?: { top: number; right: number; bottom: number; left: number };
  /**
   * Callback function when a data point is clicked
   */
  onDataPointClick?: (seriesId: string, dataPoint: DataPoint) => void;

  onLineClick?: (seriesId: string) => void;

  axisFormatters?: AxisFormatters;

  legendSpacing?: number;

  // ADD THIS ONE PROP
  formatAsQuarters?: boolean;
}

const formatXAxis = item => {
  const date = new Date(item);
  return `Q${Math.floor(date.getMonth() / 3) + 1}  ${date.getFullYear()}`;
};

const AtomLineChart: React.FC<LineChartProps> = ({
  series,
  width,
  height,
  title,
  xLabel,
  yLabel,
  className,
  showVerticalGrid = false,
  showHorizontalGrid = false,
  showTooltip = true,
  showLegend = true,
  axisFormatters,
  onDataPointClick,
  onLineClick,
  formatAsQuarters = false,
}) => {
  // Get all unique x values from all series and sort them
  const allXValues = [
    ...new Set(
      series.flatMap(s => s.data?.filter(d => d && d.x !== undefined).map(d => d.x) || [])
    ),
  ].sort((a, b) => Number(a) - Number(b));

  const xAxis = allXValues.map(d => (formatAsQuarters ? formatXAxis(d) : String(d)));

  // Transform series data to align with the complete x-axis
  const formattedSeries = series.map(s => ({
    id: s.id,
    data: allXValues.map(xVal => {
      const dataPoint = s.data?.find(d => String(d.x) === String(xVal));
      return dataPoint ? dataPoint.y : null;
    }),
    label: s.label || s.id,
    color: s.color,
    connectNulls: false,
  }));

  const seriesStyles = formattedSeries.reduce((acc, s) => {
    acc[`[class*="MuiMarkElement-series-${s.id}"]`] = {
      fill: s.color,
      stroke: s.color,
      strokeWidth: 0,
      r: 0,
    };
    return acc;
  }, {});

  return (
    <div
      className={`${styles.chartContainer} ${className || ''}`}
      style={{ width: '100%', minHeight: height ? `${height}px` : '100%' }} // Changed height to minHeight
    >
      {title && <h3 className={styles.chartTitle}>{title}</h3>}
      {console.log('Chart container minHeight:', height ? `${height}px` : '100%')} {/* Debug container height */}
      <MuiLineChart
        width={width}
        height={height || 400} // Explicit height to prevent shrinking, fallback to 400px if not provided
        series={formattedSeries}
        xAxis={[
          {
            data: xAxis,
            label: xLabel,
            scaleType: typeof xAxis[0] === 'string' ? 'band' : 'linear',
            valueFormatter: axisFormatters?.xAxisFormatter,
          },
        ]}
        yAxis={[
          {
            label: yLabel,
            valueFormatter: axisFormatters?.yAxisFormatter,
          },
        ]}
        sx={{
          ...seriesStyles,
          '.MuiLineElement-root': {
            strokeWidth: 2,
          },
        }}
        grid={{
          vertical: showVerticalGrid,
          horizontal: showHorizontalGrid,
        }}
        slotProps={{
          legend: {
            hidden: true,
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: { top: 20, bottom: 0, left: 0, right: 0 },
            itemMarkWidth: 15,
            itemMarkHeight: 15,
            markGap: 5,
            itemGap: 10,
          },
        }}
        tooltip={{
          trigger: showTooltip ? 'axis' : 'none',
        }}
        onAxisClick={(event, d) => { }}
        onLineClick={(event, d) => {
          if (onLineClick) onLineClick(d.seriesId);
        }}
        onMarkClick={(event, d) => {
          if (onDataPointClick)
            onDataPointClick(d.seriesId, {
              x: xAxis[d.index],
              y: d.value,
            });
        }}
      />
      {showLegend && (
        <div className={styles.legendContainer} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginTop: '5px', gap: '10px' }}>
          {console.log('Rendering custom legend with series:', formattedSeries)}
          {formattedSeries.map(s => (
            <div key={s.id} className={styles.legendItem} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                className={styles.legendColor}
                style={{
                  width: '12px', // Changed to square size
                  height: '12px', // Changed to match width for a square
                  backgroundColor: s.color,
                  borderRadius: '2px', // Optional: Keep slight rounding or set to 0 for sharp edges
                }}
              />
              <span>{s.label}</span>
              {console.log(`Legend item rendered for series: ${s.id} with color ${s.color}`)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AtomLineChart;
